import React, { useEffect, useRef, useState } from 'react';
import { Search, User, X as XIcon, UserPlus } from 'lucide-react';
import { apiService } from '../../../services/apiService';
import type { PatientListItem } from '../../../services/apiService';
import { useToast } from '../../../contexts/ToastContext';

interface PatientPickerProps {
    value: PatientListItem | null;
    onPick: (p: PatientListItem) => void;
    disabled?: boolean;
}

const inputClass =
    'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';
const labelClass = 'block text-[11px] font-medium uppercase tracking-wider text-brand-text-muted mb-1';

const PatientPicker: React.FC<PatientPickerProps> = ({ value, onPick, disabled }) => {
    const [query, setQuery] = useState('');
    const [open, setOpen] = useState(false);
    const [results, setResults] = useState<PatientListItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [showCreate, setShowCreate] = useState(false);
    const wrapRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open || query.trim().length < 2) {
            setResults([]);
            return;
        }
        const handle = setTimeout(async () => {
            try {
                setLoading(true);
                const res = await apiService.listPatients({ search: query, limit: 8 });
                setResults(res.data.data || []);
            } finally {
                setLoading(false);
            }
        }, 200);
        return () => clearTimeout(handle);
    }, [query, open]);

    useEffect(() => {
        if (!open) return;
        const onDoc = (e: MouseEvent) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
        };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);

    const openCreateFromQuery = () => {
        setShowCreate(true);
        setOpen(false);
    };

    if (value && !open) {
        return (
            <>
                <div className="flex items-center gap-2 px-3 py-2 bg-brand-surface-2/60 border border-brand-border rounded-md">
                    <User className="h-4 w-4 text-brand-text-muted shrink-0" strokeWidth={1.75} aria-hidden />
                    <div className="flex-1 min-w-0">
                        <div className="text-sm font-medium text-brand-text truncate">{value.full_name}</div>
                        <div className="text-xs text-brand-text-muted tabular-nums">{value.patient_number}{value.email && <> · {value.email}</>}</div>
                    </div>
                    {!disabled && (
                        <button
                            type="button"
                            onClick={() => { setQuery(''); setOpen(true); }}
                            className="text-xs font-medium text-brand-primary hover:text-brand-primary-hover transition-colors cursor-pointer"
                        >
                            Ändern
                        </button>
                    )}
                </div>
                {showCreate && (
                    <CreatePatientModal
                        initialQuery={query}
                        onClose={() => setShowCreate(false)}
                        onCreated={(p) => { setShowCreate(false); setQuery(''); onPick(p); }}
                    />
                )}
            </>
        );
    }

    return (
        <>
            <div ref={wrapRef} className="relative">
                <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-text-muted pointer-events-none" strokeWidth={1.75} aria-hidden />
                    <input
                        type="text"
                        autoFocus
                        value={query}
                        onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
                        onFocus={() => setOpen(true)}
                        placeholder="Patient suchen (Name, E-Mail, NIE, Telefon)…"
                        className={`${inputClass} pl-9 pr-9`}
                        disabled={disabled}
                    />
                    {value && (
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-brand-text-muted hover:text-brand-text cursor-pointer"
                            title="Abbrechen"
                        >
                            <XIcon className="h-3.5 w-3.5" strokeWidth={1.75} />
                        </button>
                    )}
                </div>

                {open && (
                    <div className="absolute z-30 mt-1 w-full rounded-md bg-brand-surface border border-brand-border shadow-lg max-h-80 overflow-y-auto">
                        {query.trim().length < 2 ? (
                            <div className="px-3 py-3 text-xs text-brand-text-muted">Mindestens 2 Zeichen eingeben oder neuen Patienten anlegen.</div>
                        ) : loading ? (
                            <div className="px-3 py-3 text-xs text-brand-text-muted">Suche…</div>
                        ) : results.length === 0 ? (
                            <div className="px-3 py-3 text-xs text-brand-text-muted">
                                Keine Treffer für „{query}".
                            </div>
                        ) : (
                            <ul role="listbox">
                                {results.map((p) => (
                                    <li key={p.id}>
                                        <button
                                            type="button"
                                            onClick={() => { onPick(p); setOpen(false); setQuery(''); }}
                                            className="w-full text-left px-3 py-2 hover:bg-brand-surface-2/60 transition-colors cursor-pointer flex items-center gap-3"
                                        >
                                            <User className="h-4 w-4 text-brand-text-muted shrink-0" strokeWidth={1.75} aria-hidden />
                                            <div className="min-w-0">
                                                <div className="text-sm font-medium text-brand-text truncate">{p.full_name}</div>
                                                <div className="text-xs text-brand-text-muted truncate">
                                                    {p.patient_number}{p.email && <> · {p.email}</>}{p.phone_primary && <> · {p.phone_primary}</>}
                                                </div>
                                            </div>
                                        </button>
                                    </li>
                                ))}
                            </ul>
                        )}

                        {/* Always-visible footer action: anlegen ist kein Sonderfall, sondern legitimer Pfad */}
                        <div className="border-t border-brand-border bg-brand-surface-2/30">
                            <button
                                type="button"
                                onClick={openCreateFromQuery}
                                className="w-full flex items-center gap-2 px-3 py-2.5 text-sm font-medium text-brand-primary hover:bg-brand-surface-2 transition-colors cursor-pointer"
                            >
                                <UserPlus className="h-4 w-4" strokeWidth={1.75} />
                                <span>Neuen Patienten anlegen{query.trim().length >= 2 ? <> &bdquo;{query}&ldquo;</> : null}</span>
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {showCreate && (
                <CreatePatientModal
                    initialQuery={query}
                    onClose={() => setShowCreate(false)}
                    onCreated={(p) => { setShowCreate(false); setQuery(''); onPick(p); }}
                />
            )}
        </>
    );
};

// ====================================================================
// CreatePatientModal — Inline-Quick-Anleger
// Minimalfelder: First/Last Name, E-Mail, Telefon, Sprache. Rest später.
// ====================================================================

interface CreatePatientModalProps {
    initialQuery: string;
    onClose: () => void;
    onCreated: (p: PatientListItem) => void;
}

const splitInitialName = (q: string): { first: string; last: string } => {
    const trimmed = q.trim();
    if (!trimmed) return { first: '', last: '' };
    // Wenn das Query wie eine E-Mail aussieht oder eine Telefonnummer → leer lassen.
    if (trimmed.includes('@') || /^[+\d\s()-]+$/.test(trimmed)) return { first: '', last: '' };
    const parts = trimmed.split(/\s+/);
    if (parts.length === 1) return { first: parts[0], last: '' };
    return { first: parts[0], last: parts.slice(1).join(' ') };
};

const splitInitialContact = (q: string): { email: string; phone: string } => {
    const trimmed = q.trim();
    if (trimmed.includes('@')) return { email: trimmed, phone: '' };
    if (/^[+\d\s()-]{4,}$/.test(trimmed)) return { email: '', phone: trimmed };
    return { email: '', phone: '' };
};

const CreatePatientModal: React.FC<CreatePatientModalProps> = ({ initialQuery, onClose, onCreated }) => {
    const { addToast } = useToast();
    const initName = splitInitialName(initialQuery);
    const initContact = splitInitialContact(initialQuery);
    const [firstName, setFirstName] = useState(initName.first);
    const [lastName, setLastName] = useState(initName.last);
    const [email, setEmail] = useState(initContact.email);
    const [phone, setPhone] = useState(initContact.phone);
    const [language, setLanguage] = useState<'de' | 'en' | 'es' | 'ru'>('de');
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    const canSubmit = (firstName.trim().length >= 1 && lastName.trim().length >= 1) || firstName.trim().length >= 2;

    const submit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!canSubmit || submitting) return;
        setErr(null);
        setSubmitting(true);
        try {
            const full_name = [firstName.trim(), lastName.trim()].filter(Boolean).join(' ').trim();
            const payload: any = {
                full_name,
                first_name: firstName.trim() || null,
                last_name: lastName.trim() || null,
                email: email.trim() || null,
                phone_primary: phone.trim() || null,
                preferred_language: language,
                source: 'admin',
            };
            const res = await apiService.createPatient(payload);
            const created = res.data.data as any;
            addToast(`Patient ${created.patient_number || ''} angelegt`, 'success');
            // PatientListItem braucht appointment_count / last_appointment_date — bei
            // frisch angelegtem Patienten sind beide trivial.
            const asListItem: PatientListItem = {
                ...created,
                appointment_count: 0,
                last_appointment_date: null,
            };
            onCreated(asListItem);
        } catch (e: any) {
            const msg = e?.response?.data?.message || 'Patient konnte nicht angelegt werden';
            setErr(msg);
            addToast(msg, 'error');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
            <form
                onSubmit={submit}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-md rounded-xl bg-brand-surface border border-brand-border shadow-xl p-5"
            >
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-base font-semibold text-brand-text flex items-center gap-2">
                        <UserPlus className="h-4 w-4 text-brand-primary" strokeWidth={1.75} />
                        Neuer Patient
                    </h3>
                    <button
                        type="button"
                        onClick={onClose}
                        className="p-1 text-brand-text-muted hover:text-brand-text rounded cursor-pointer"
                        title="Schließen"
                    >
                        <XIcon className="h-4 w-4" strokeWidth={1.75} />
                    </button>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className={labelClass}>Vorname</label>
                        <input
                            autoFocus
                            type="text"
                            value={firstName}
                            onChange={(e) => setFirstName(e.target.value)}
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Nachname</label>
                        <input
                            type="text"
                            value={lastName}
                            onChange={(e) => setLastName(e.target.value)}
                            className={inputClass}
                        />
                    </div>
                    <div className="col-span-2">
                        <label className={labelClass}>E-Mail</label>
                        <input
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="optional"
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Telefon</label>
                        <input
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            placeholder="optional"
                            className={inputClass}
                        />
                    </div>
                    <div>
                        <label className={labelClass}>Sprache</label>
                        <select
                            value={language}
                            onChange={(e) => setLanguage(e.target.value as any)}
                            className={inputClass}
                        >
                            <option value="de">Deutsch</option>
                            <option value="es">Español</option>
                            <option value="en">English</option>
                            <option value="ru">Русский</option>
                        </select>
                    </div>
                </div>

                {err && (
                    <div className="mt-3 text-xs text-rose-600">{err}</div>
                )}

                <p className="mt-3 text-[11px] text-brand-text-muted">
                    Adresse und NIE können später in der Patientenakte ergänzt werden.
                </p>

                <div className="mt-5 flex items-center justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="px-3 py-2 text-sm font-medium text-brand-text-muted hover:text-brand-text rounded-md cursor-pointer"
                    >
                        Abbrechen
                    </button>
                    <button
                        type="submit"
                        disabled={!canSubmit || submitting}
                        className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <UserPlus className="h-3.5 w-3.5" strokeWidth={2} />
                        {submitting ? 'Lege an…' : 'Anlegen & verwenden'}
                    </button>
                </div>
            </form>
        </div>
    );
};

export default PatientPicker;
